import { useState } from 'react';
import StatusMessage from '../components/StatusMessage';
import UsersList from '../components/UsersList';
import {
  exportSchoolUsersToExcel,
  exportSingleUserToExcel,
} from '../utils/exportToExcel';
import { generateAllSchoolCards } from '../utils/bulkCardGenerator';

const resolveExportVariant = (permissions) => {
  if (permissions?.canConfirmPrinting) {
    return 'printing';
  }
  if (permissions?.canAddEmail) {
    return 'add-email';
  }
  if (permissions?.canPaperCat) {
    return 'paper-cat';
  }
  return 'printing';
};

function SchoolDetailsPage({
  user,
  selectedSchool,
  usersState,
  permissions,
  onBack,
  onLogout,
}) {
  const [activeTab, setActiveTab] = useState('teachers'); // 'teachers' | 'managers'
  const [cardExpiryDate, setCardExpiryDate] = useState('');
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [sortKey, setSortKey] = useState('status-asc'); // 'status-asc' (pending first)
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'manager', 'SupervisorSchool', 'superManager'
  
  const schoolName =
    selectedSchool?.schoolName || selectedSchool?.name || 'مدرسة غير معروفة';
  const userName = user?.fullName || user?.userId || 'مستخدم';
  const groupName = selectedSchool?.GroupName || selectedSchool?.groupName;
  const schoolId = selectedSchool?.schoolId || selectedSchool?._id;
  
  const currentUsers = activeTab === 'teachers' ? usersState.users : usersState.managers;
  
  // تطبيق الفلترة والترتيب لجميع المستخدمين
  const processedUsers = currentUsers
    .filter(u => {
      const search = filterText.toLowerCase();
      const name = (u.userName || u.fullName || u.name || '').toLowerCase();
      const id = (u.userIdNumber || '').toLowerCase();
      const role = (u.role || u.userRole || u.jobTitle || u.position || u.userPosition || '').toLowerCase();
      const matchesText = name.includes(search) || id.includes(search) || role.includes(search);
      
      const matchesRole = activeTab === 'managers' 
        ? (roleFilter === 'all' || role === roleFilter.toLowerCase() || 
           (u.role === roleFilter) || (u.userRole === roleFilter) || 
           (u.jobTitle === roleFilter))
        : true;
      
      return matchesText && matchesRole;
    })
    .sort((a, b) => {
      if (sortKey.startsWith('name')) {
        const nameA = (a.userName || a.fullName || a.name || '');
        const nameB = (b.userName || b.fullName || b.name || '');
        const result = nameA.localeCompare(nameB, 'ar');
        return sortKey.endsWith('desc') ? -result : result;
      }
      if (sortKey.startsWith('id')) {
        const idA = (a.userIdNumber || '');
        const idB = (b.userIdNumber || '');
        const result = idA.localeCompare(idB);
        return sortKey.endsWith('desc') ? -result : result;
      }
      if (sortKey.startsWith('status')) {
        let statusField = 'printingCheck';
        if (permissions.canConfirmPrinting) statusField = 'printingCheck';
        else if (permissions.canAddEmail) statusField = 'AddEmailCheck';
        else if (permissions.canPaperCat) statusField = 'paperCat';
        else if (permissions.canEndAll) statusField = 'endCheck';

        const valA = a[statusField] ? 1 : 0;
        const valB = b[statusField] ? 1 : 0;
        const result = valA - valB;
        // status-asc means pending first, status-desc means completed first
        return sortKey.endsWith('desc') ? -result : result;
      }
      return 0;
    });

  const currentTotal = activeTab === 'teachers' 
    ? (filterText ? processedUsers.length : usersState.total) 
    : (activeTab === 'managers' && (filterText || roleFilter !== 'all') ? processedUsers.length : usersState.managersTotal);
  const currentStatus = activeTab === 'teachers' ? usersState.status : usersState.managersStatus;
  
  const totalUsers = currentTotal || processedUsers?.length || 0;
  const hasUsers = Array.isArray(processedUsers) && processedUsers.length > 0;
  const exportVariant = resolveExportVariant(permissions);
  const exportTooltipMap = {
    printing: 'تصدير بيانات الطباعة',
    'add-email': 'تصدير بيانات إضافة البريد',
    'paper-cat': 'تصدير بيانات papercut',
  };
  const exportTooltip = exportTooltipMap[exportVariant] || exportTooltipMap.printing;
  
  // تحويل التاريخ للصيغة المطلوبة: يوم\شهر\سنة (11\7\2026)
  const formattedExpiryDate = cardExpiryDate ? (() => {
    const dateObj = new Date(cardExpiryDate);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    return `${day}\\${month}\\${year}`;
  })() : null;

  const handleExportSchool = () => {
    if (!hasUsers) {
      return;
    }
    exportSchoolUsersToExcel({
      users: currentUsers,
      school: selectedSchool,
      variant: exportVariant,
    });
  };

  const handleExportSingleUser = (userRecord) => {
    exportSingleUserToExcel({
      user: userRecord,
      school: selectedSchool,
      variant: exportVariant,
    });
  };

  const handleGenerateAllCards = async () => {
    if (!hasUsers) {
      return;
    }

    setIsGeneratingAll(true);
    setBulkProgress({ current: 0, total: currentUsers.length, percentage: 0 });

    try {
      const result = await generateAllSchoolCards(
        currentUsers,
        schoolName,
        formattedExpiryDate,
        (progress) => {
          setBulkProgress(progress);
        }
      );

      // إظهار رسالة النجاح
      alert(`✅ تم توليد ${result.success} بطاقة بنجاح!${result.errors > 0 ? `\n⚠️ فشل توليد ${result.errors} بطاقة (انظر ملف errors.txt في الملف المضغوط)` : ''}`);
    } catch (error) {
      console.error('خطأ في توليد البطاقات:', error);
      alert(`❌ خطأ: ${error.message}`);
    } finally {
      setIsGeneratingAll(false);
      setBulkProgress(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilterText('');
    setSortKey('none');
    setRoleFilter('all');
    if (tab === 'managers' && usersState.managers.length === 0 && usersState.managersStatus.type !== 'loading') {
      usersState.fetchManagers(selectedSchool);
    }
  };

  return (
    <div className="card home-card">
      <div className="card-header details-header">
        <div className="details-heading">
          <h1>المستخدم: {userName}</h1>
          <p className="school-line">
            <span>المدرسة المختارة: {schoolName}</span>
            <button
              type="button"
              className="icon-button"
              onClick={handleExportSchool}
              title={`${exportTooltip} لكل مستخدمي المدرسة`}
              aria-label="تصدير بيانات المدرسة إلى ملف Excel"
              disabled={!hasUsers}
            >
              📊
            </button>
            {permissions?.canConfirmPrinting && (
              <button
                type="button"
                className="icon-button"
                onClick={handleGenerateAllCards}
                title="تنزيل جميع بطاقات المدرسة (ZIP)"
                aria-label="توليد وتنزيل جميع بطاقات مستخدمي المدرسة في ملف مضغوط"
                disabled={!hasUsers || isGeneratingAll}
              >
                {isGeneratingAll ? '⏳' : '📦'}
              </button>
            )}
          </p>
        </div>
        <div className="actions-group">
          <button type="button" className="secondary" onClick={onBack}>
            عودة إلى المدارس
          </button>
          <button type="button" className="secondary" onClick={onLogout}>
            تسجيل خروج
          </button>
        </div>
      </div>

      <section className="panel">
        <div className="panel-header users-panel-header">
          <div className="users-header-row">
            <h2>مستخدمي المدرسة ({totalUsers})</h2>
            <span>
              {groupName ? `مجموعة: ${groupName}` : 'يتم عرض المستخدمين النشطين'}
            </span>
          </div>
          
          <div className="tabs-container">
            <button
              type="button"
              className={`tab-button ${activeTab === 'teachers' ? 'active' : ''}`}
              onClick={() => handleTabChange('teachers')}
            >
              الأساتذة
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'managers' ? 'active' : ''}`}
              onClick={() => handleTabChange('managers')}
            >
              الإداريين
            </button>
          </div>

          <div className="filters-container">
            <div className="search-wrapper">
              <input
                type="text"
                className="filter-input"
                placeholder="بحث بالاسم أو المعرف..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>
            
            <div className="filters-actions-wrapper">
              <div className="filter-group">
                <label>الترتيب:</label>
                <select
                  className="filter-select"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                >
                  <option value="none">بدون ترتيب</option>
                  <option value="name-asc">الاسم (↑)</option>
                  <option value="name-desc">الاسم (↓)</option>
                  <option value="id-asc">المعرف (↑)</option>
                  <option value="id-desc">المعرف (↓)</option>
                  <option value="status-desc">المكتمل أولاً (✓)</option>
                  <option value="status-asc">غير المكتمل أولاً (✗)</option>
                </select>
              </div>
 
              {activeTab === 'managers' && (
                <div className="filter-group">
                  <label>الصفة:</label>
                  <select
                    className="filter-select"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value="all">الكل</option>
                    <option value="manager">معاون</option>
                    <option value="SupervisorSchool">منسق تقني</option>
                    <option value="superManager">مدير المدرسة</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
         
        {permissions?.canConfirmPrinting && (
          <>
            <div style={{
              padding: '15px',
              background: '#f0f8ff',
              borderRadius: '8px',
              margin: '10px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              border: '1px solid #d0e7ff'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                <span>📅 تاريخ انتهاء صلاحية البطاقات:</span>
                <input
                  type="date"
                  value={cardExpiryDate}
                  onChange={(e) => setCardExpiryDate(e.target.value)}
                  style={{ 
                    padding: '6px 10px', 
                    fontSize: '14px', 
                    border: '1px solid #ccc', 
                    borderRadius: '4px',
                    direction: 'ltr'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setCardExpiryDate('2026-07-01')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    color: '#fff',
                    backgroundColor: '#4CAF50',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  title="تعيين التاريخ إلى 1/7/2026"
                >
                  📆 نهاية السنة الحالية
                </button>
              </label>
              {formattedExpiryDate && (
                <span style={{ 
                  fontSize: '12px', 
                  color: '#666',
                  backgroundColor: '#fff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}>
                  سيُطبع: نافذ لغاية تاريخ {formattedExpiryDate}
                </span>
              )}
            </div>
            
            {bulkProgress && (
              <div style={{
                padding: '15px',
                background: '#fff3cd',
                borderRadius: '8px',
                margin: '10px 0',
                border: '1px solid #ffc107'
              }}>
                <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                  جاري توليد البطاقات... {bulkProgress.percentage}%
                </div>
                <div style={{ 
                  width: '100%', 
                  height: '20px', 
                  backgroundColor: '#e0e0e0', 
                  borderRadius: '10px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${bulkProgress.percentage}%`,
                    height: '100%',
                    backgroundColor: '#4CAF50',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  {bulkProgress.current} من {bulkProgress.total} - {bulkProgress.currentUser}
                </div>
              </div>
            )}
          </>
        )}
        <StatusMessage type={currentStatus?.type} message={currentStatus?.message} />
        {usersState.actionStatus?.type !== 'idle' && (
          <StatusMessage
            type={usersState.actionStatus?.type}
            message={usersState.actionStatus?.message}
          />
        )}
        {usersState.nfcStatus?.type !== 'idle' && (
          <StatusMessage
            type={usersState.nfcStatus?.type}
            message={usersState.nfcStatus?.message}
          />
        )}
        {usersState.emailStatus?.type !== 'idle' && (
          <StatusMessage
            type={usersState.emailStatus?.type}
            message={usersState.emailStatus?.message}
          />
        )}
        {usersState.paperCatStatus?.type !== 'idle' && (
          <StatusMessage
            type={usersState.paperCatStatus?.type}
            message={usersState.paperCatStatus?.message}
          />
        )}
        {usersState.endStatus?.type !== 'idle' && (
          <StatusMessage
            type={usersState.endStatus?.type}
            message={usersState.endStatus?.message}
          />
        )}
        {currentStatus?.type === 'success' && (
          <UsersList
            users={processedUsers}
            canConfirmPrinting={permissions?.canConfirmPrinting}
            onConfirmPrinting={usersState.confirmPrinting}
            confirmingId={usersState.confirmingId}
            canUpdateNfc={permissions?.canConfirmPrinting}
            onUpdateNfc={usersState.updateNfcId}
            updatingNfcId={usersState.nfcUpdatingId}
            canConfirmEmail={permissions?.canAddEmail}
            onConfirmEmail={usersState.confirmAddEmail}
            confirmingEmailId={usersState.confirmingEmailId}
            canConfirmPaperCat={permissions?.canPaperCat}
            onConfirmPaperCat={usersState.confirmPaperCat}
            confirmingPaperCatId={usersState.confirmingPaperCatId}
            canConfirmEndAll={permissions?.canEndAll}
            onConfirmEndAll={usersState.confirmEndAll}
            confirmingEndAllId={usersState.confirmingEndId}
            onExportUser={handleExportSingleUser}
            exportTooltip={exportTooltip}
            schoolName={schoolName}
            cardExpiryDate={formattedExpiryDate}
          />
        )}
      </section>
    </div>
  );
}

export default SchoolDetailsPage;

